import { useMemo } from "react";
import { AlertTriangle, Target } from "lucide-react";

import type {
  CoverageMatrix,
  CoverageRequirement,
  CoverageRequirementStatus,
} from "../../domain/types";
import { useDebateStore } from "../../store/debateStore";
import { cn } from "../common/cn";

/**
 * Kalite Planı — `coverage.update` matrisinin verdict sekmesi görünümü
 * + canlı transcript üstü kompakt kapsam çubuğu (CoverageLiveBar).
 *
 * Veri kaynağı önceliği VerdictPanel'de çözülür: verdict JSON'undaki
 * `coverage_matrix` (final) > canlı SSE matrisi (son snapshot). İkisi
 * de yoksa sekme HİÇ render edilmez — legacy davranış korunur.
 */

const STATUS_META: Record<
  CoverageRequirementStatus,
  { glyph: string; label: string; text: string; chip: string }
> = {
  covered: {
    glyph: "✓",
    label: "Kapatıldı",
    text: "text-emerald-300",
    chip: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  },
  partial: {
    glyph: "~",
    label: "Kısmi",
    text: "text-amber-300",
    chip: "border-amber-400/45 bg-amber-500/10 text-amber-200",
  },
  uncovered: {
    glyph: "○",
    label: "Açık",
    text: "text-rose-300",
    chip: "border-rose-500/45 bg-rose-500/10 text-rose-200",
  },
};

function prettyDeliverableType(raw: string): string {
  const s = raw.trim().replace(/[_-]+/g, " ");
  return s.length > 0 ? s : "Çıktı Kapsamı";
}

function countByStatus(requirements: CoverageRequirement[]) {
  let covered = 0;
  let partial = 0;
  let uncovered = 0;
  for (const r of requirements) {
    if (r.status === "covered") covered += 1;
    else if (r.status === "partial") partial += 1;
    else uncovered += 1;
  }
  return { covered, partial, uncovered, total: requirements.length };
}

function scorePct(score: number): number {
  return Math.round(Math.max(0, Math.min(1, score)) * 100);
}

function RequirementRow({ req }: { req: CoverageRequirement }) {
  const meta = STATUS_META[req.status];
  const evidenceCount = req.evidenceTurnIds.length;
  return (
    <li className="flex items-start gap-3 rounded-lg border border-white/[0.05] bg-surface-1/40 px-3 py-2.5">
      <span
        className={cn(
          "grid h-5 w-5 flex-none place-items-center rounded-full border text-[12px] font-bold leading-none",
          meta.chip,
        )}
        title={meta.label}
        aria-label={meta.label}
      >
        {meta.glyph}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-xs leading-snug text-fg-strong">{req.text}</p>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-fg-subtle">
          {req.kind ? (
            <span className="rounded-full border border-white/[0.08] bg-surface-2/60 px-1.5 py-0.5 uppercase tracking-wider">
              {req.kind}
            </span>
          ) : null}
          <span className={cn("font-medium uppercase tracking-wider", meta.text)}>
            {meta.label}
          </span>
          <span
            className="font-mono tabular-nums"
            title={
              evidenceCount > 0
                ? `Bu gereksinimi destekleyen ${evidenceCount} konuşma turu var.`
                : "Henüz hiçbir konuşma turu bu gereksinimi kanıtlamadı."
            }
          >
            · {evidenceCount} kanıt turu
          </span>
        </div>
      </div>
    </li>
  );
}

/**
 * Gevşek tipli `consistency_flags` kayıtlarını görüntülenebilir satıra
 * indirger — bilinen string alan adlarını sırayla dener, hiçbiri yoksa
 * kaydın kompakt JSON'unu gösterir (bilgi kaybetmeden).
 */
function flagText(flag: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = flag[k];
    if (typeof v === "string" && v.trim().length > 0) return v;
  }
  return "";
}

function ConsistencyFlagsSection({
  flags,
}: {
  flags: Record<string, unknown>[];
}) {
  if (flags.length === 0) return null;
  return (
    <section className="rounded-xl border border-amber-400/25 bg-amber-500/[0.05] p-4">
      <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-amber-300">
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
        Tutarlılık Bayrakları ({flags.length})
      </h3>
      <p className="mb-3 text-[11px] text-fg-subtle">
        Konsey çıktısında kod tarafından tespit edilen çelişki / tutarsızlık
        sinyalleri. Karara güvenmeden önce gözden geçirin.
      </p>
      <ul className="flex flex-col gap-1.5">
        {flags.map((flag, i) => {
          const kind = flagText(flag, ["kind", "type", "category"]);
          const detail = flagText(flag, [
            "detail",
            "description",
            "message",
            "text",
          ]);
          return (
            <li
              key={`flag-${i}`}
              className="flex items-start gap-2 rounded-lg border border-white/[0.05] bg-surface-1/40 px-3 py-2 text-xs"
            >
              <AlertTriangle
                className="mt-0.5 h-3 w-3 flex-none text-amber-300"
                aria-hidden
              />
              <div className="min-w-0 flex-1">
                {kind ? (
                  <span className="mr-1.5 rounded-full border border-amber-400/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-amber-200">
                    {kind}
                  </span>
                ) : null}
                <span className="break-words text-fg-muted">
                  {detail || JSON.stringify(flag)}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export interface CoveragePanelProps {
  matrix: CoverageMatrix;
  consistencyFlags?: Record<string, unknown>[];
}

export function CoveragePanel({
  matrix,
  consistencyFlags = [],
}: CoveragePanelProps) {
  const { covered, partial, uncovered, total } = countByStatus(
    matrix.requirements,
  );
  const pct = scorePct(matrix.score);
  const barColor =
    pct >= 85
      ? "from-emerald-500 via-emerald-400 to-teal-300"
      : pct >= 60
        ? "from-amber-500 via-amber-400 to-yellow-300"
        : "from-rose-500 via-rose-400 to-orange-300";

  // Açık kalan işler önce görünsün: uncovered → partial → covered.
  const ordered = useMemo(() => {
    const rank: Record<CoverageRequirementStatus, number> = {
      uncovered: 0,
      partial: 1,
      covered: 2,
    };
    return [...matrix.requirements].sort(
      (a, b) => rank[a.status] - rank[b.status],
    );
  }, [matrix.requirements]);

  return (
    <div className="flex flex-col gap-4">
      <header className="rounded-xl border border-white/[0.06] bg-surface-1/40 p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold capitalize text-fg-strong">
            <Target className="h-4 w-4 text-accent-300" aria-hidden />
            {prettyDeliverableType(matrix.deliverableType)}
          </h3>
          <span className="font-mono text-2xl font-bold tabular-nums text-fg-strong">
            %{pct}
          </span>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/[0.08]">
          <div
            className={cn("h-full rounded-full bg-gradient-to-r", barColor)}
            style={{ width: `${pct}%` }}
            aria-hidden
          />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-fg-subtle">
          <span className="text-emerald-300">✓ {covered} kapatıldı</span>
          <span className="text-amber-300">~ {partial} kısmi</span>
          <span className="text-rose-300">○ {uncovered} açık</span>
          <span className="ml-auto font-mono tabular-nums">
            {covered}/{total} gereksinim
          </span>
        </div>
      </header>

      <ul className="flex flex-col gap-2" aria-label="Gereksinim kapsama listesi">
        {ordered.map((req) => (
          <RequirementRow key={req.id} req={req} />
        ))}
      </ul>

      <ConsistencyFlagsSection flags={consistencyFlags} />
    </div>
  );
}

/**
 * CoverageLiveBar — canlı transcript'in üstünde duran tek satırlık
 * kompakt kapsam göstergesi ("X/Y kapatıldı"). İlk `coverage.update`
 * gelene kadar hiç render edilmez; layout'ta yer kaplamaz.
 */
export function CoverageLiveBar() {
  const matrix = useDebateStore((s) => s.coverageMatrix);
  if (!matrix || matrix.requirements.length === 0) return null;

  const { covered, partial, total } = countByStatus(matrix.requirements);
  const pct = scorePct(matrix.score);
  const openReqs = matrix.requirements
    .filter((r) => r.status !== "covered")
    .map((r) => `${r.status === "partial" ? "~" : "○"} ${r.text}`);
  const title =
    openReqs.length > 0
      ? `Açık gereksinimler:\n${openReqs.join("\n")}`
      : "Tüm gereksinimler kapatıldı.";

  return (
    <div
      className="flex items-center gap-2.5 border-b border-white/[0.06] px-4 py-2 sm:px-5"
      title={title}
      role="status"
      aria-label={`Kapsam: ${covered}/${total} gereksinim kapatıldı`}
    >
      <Target className="h-3.5 w-3.5 flex-none text-accent-300" aria-hidden />
      <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-fg-subtle">
        Kapsam
      </span>
      <div className="h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-white/[0.08]">
        <div
          className={cn(
            "h-full rounded-full bg-gradient-to-r",
            pct >= 85
              ? "from-emerald-500 to-teal-300"
              : pct >= 60
                ? "from-amber-500 to-yellow-300"
                : "from-rose-500 to-orange-300",
          )}
          style={{ width: `${pct}%` }}
          aria-hidden
        />
      </div>
      <span className="flex-none font-mono text-[10px] tabular-nums text-fg-subtle">
        <span className="font-medium text-fg-strong">{covered}</span>/{total}{" "}
        kapatıldı
        {partial > 0 ? <span className="text-amber-300"> · {partial} kısmi</span> : null}
        <span className="ml-1.5 text-fg-subtle">%{pct}</span>
      </span>
    </div>
  );
}
